import { useMemo, useState } from 'react'
import type { ProjectSummary } from '../lib/db'
import { Loading, Sheet } from './bits'

import DayPlan from './DayPlan'
import type { CrewMember } from '../lib/crew'
import { awayOn, crewWording, loadCrew, namesFor, nameOf, saveCrew, validAway } from '../lib/crew'

import { loadProject, saveProject } from '../lib/db'
import {
  clock,
  conflicts,
  dayLabel,
  dayNumber,
  lengthOf,
  newVisit,
  onDay,
  shiftWeek,
  stateWording,
  unscheduled,
  validWindow,
  VISIT_KINDS,
  weekDays,
  weekStart,
  windowOf,
  type JobRef,
  type Visit,
  type VisitKind,
} from '../lib/visits'

/**
 * THE WEEK, AND WHAT IS NOT ON IT.
 *
 * Two halves, and the right hand one is the point. A calendar shows what has
 * been decided; the queue shows what has not, and a season is lost to the
 * second one rather than the first. A job with an agreed scope and no install
 * booked is the single most expensive thing in this product to forget, so it
 * is on screen next to the week rather than behind a filter.
 *
 * SEVEN ROWS, NOT SEVEN COLUMNS. A column layout gives each day about ninety
 * pixels on a laptop, which is not enough for a time and a customer's name
 * side by side, and it collapses into something else entirely on a phone. Days
 * down the page hold the same shape at 1440 and at 390, and it is the shape
 * somebody actually reads: what is on Tuesday.
 *
 * EVERYTHING HERE IS A REAL RECORD. The visit is the first thing in LightsOS
 * a person types rather than something computed off what happened, and
 * lib/visits.ts carries the three rules that keep it honest: a window is the
 * wall clock at the house and never an instant, asked for is not agreed, and a
 * conflict check that cannot check travel, crews or stock says so on the
 * screen instead of showing a green tick.
 */
export default function Schedule({
  items,
  onChanged,
  onOpenJob,
}: {
  items: ProjectSummary[] | null
  onChanged: () => void
  onOpenJob: (job: ProjectSummary) => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  /*
   * WHO IS OUT IS A DAILY OPERATION, SO IT IS EDITED HERE.
   *
   * The roster used to be read once, because this screen only ever looked at
   * it. That was true of the names. It was never true of the away dates: you
   * learn somebody is out from a text message at six in the morning, and
   * saying so was three levels down under Settings, Team, find the person,
   * add a row - for the one fact that decides whether the week you are
   * looking at is real.
   *
   * The roster itself stays under Settings, because who works here changes
   * twice a year. Who is out this week changes on a Tuesday.
   */
  const [roster, setRoster] = useState(() => loadCrew())
  const isAway = useMemo(
    () => (id: string, day: string) => {
      const m = roster.find((x) => x.id === id)
      return !!m && !!awayOn(m, day)
    },
    [roster],
  )
  const [marking, setMarking] = useState(false)

  const [week, setWeek] = useState(() => weekStart(today))
  const [editing, setEditing] = useState<{ visit: Visit; job: ProjectSummary } | null>(null)
  const [busy, setBusy] = useState('')
  /* ROUTE-01, the honest half of it. See ui/DayPlan.tsx. */
  const [planning, setPlanning] = useState<string | null>(null)

  const jobs = useMemo(() => items ?? [], [items])

  /** Every visit on every job, flattened, with the job kept alongside. */
  const all = useMemo(
    () => jobs.flatMap((j) => (j.visits ?? []).map((v) => ({ visit: v, job: j }))),
    [jobs],
  )
  const visits = useMemo(() => all.map((x) => x.visit), [all])

  const refs = useMemo<JobRef[]>(
    () =>
      jobs.map((j) => ({
        id: j.id,
        customer: j.customer,
        address: j.address,
        name: j.name,
        scopeApproved: j.work?.approved ?? false,
      })),
    [jobs],
  )
  const queue = useMemo(() => unscheduled(refs, visits), [refs, visits])

  const days = weekDays(week)
  const who = (j: { customer: string; address: string; name: string }) =>
    j.customer.trim() || j.address.trim() || j.name

  /*
   * WRITING A VISIT MEANS WRITING THE JOB IT BELONGS TO.
   *
   * A visit lives on its job's document rather than in a table of its own, so
   * saving one is a read, a change and a save of that document. The list is
   * re-read afterwards rather than patched in place, because the summary a row
   * is drawn from is derived at save time.
   */
  const write = async (jobId: string, next: (v: Visit[]) => Visit[]) => {
    setBusy('Saving...')
    const doc = await loadProject(jobId)
    if (!doc) {
      setBusy('That job could not be opened on this device.')
      setTimeout(() => setBusy(''), 3000)
      return
    }
    await saveProject({ ...doc, visits: next(doc.visits ?? []) })
    setBusy('')
    onChanged()
  }

  const book = async (jobId: string, kind: VisitKind, day: string) => {
    const v = newVisit({
      id: `v_${Date.now().toString(36)}`,
      jobId,
      kind,
      day,
      at: new Date().toISOString(),
    })
    const job = jobs.find((j) => j.id === jobId)
    /*
     * The sheet opens on the record that was just written, not on a copy of
     * it, so pressing Remove in the sheet undoes the booking cleanly rather
     * than leaving an eight o'clock nobody asked for on the week.
     */
    if (job) setEditing({ visit: v, job })
    await write(jobId, (list) => [...list, v])
  }

  const save = async (v: Visit) => {
    await write(v.jobId, (list) =>
      list.some((x) => x.id === v.id) ? list.map((x) => (x.id === v.id ? v : x)) : [...list, v],
    )
    setEditing(null)
  }

  const drop = async (v: Visit) => {
    await write(v.jobId, (list) => list.filter((x) => x.id !== v.id))
    setEditing(null)
  }

  if (items === null) return <Loading what="Reading your jobs" />

  const booked = visits.filter((v) => days.includes(v.day)).length
  const span = `${dayLabel(days[0])} to ${dayLabel(days[6])}`

  return (
    <>
      <div className="ws-head">
        <div>
          <h1>The week</h1>
          <p>
            {span}. {booked === 0 ? 'Nothing booked yet.' : booked === 1 ? 'One visit booked.' : `${booked} visits booked.`}
          </p>
        </div>
        <div className="ws-head-acts">
          {/*
            PLANNING A DAY IS A DIFFERENT ACT FROM BOOKING ONE VISIT, so it is
            its own button rather than a mode on the week. It writes real
            visits at the end and nothing before that.
          */}
          <button className="btn" onClick={() => setPlanning(today)}>
            Plan a day
          </button>
          {/*
            A PAGER, NOT A SEGMENTED CONTROL. The pair that switches the
            customer list between List and Board marks one of the two as
            chosen; none of these three is a state, they are all moves, so
            they are not drawn as though one were selected.
          */}
          <div className="weekpager">
            <button onClick={() => setWeek(shiftWeek(week, -1))} aria-label="The week before">
              &lsaquo;
            </button>
            <button className="weekpager-now" onClick={() => setWeek(weekStart(today))}>
              This week
            </button>
            <button onClick={() => setWeek(shiftWeek(week, 1))} aria-label="The week after">
              &rsaquo;
            </button>
          </div>
        </div>
      </div>

      {busy && <p className="ws-said">{busy}</p>}

      <div className="sched">
        <div className="card sched-week">
          {days.map((d) => {
            const list = onDay(d, visits)
            return (
              <section
                key={d}
                className={[
                  'sday',
                  d === today ? 'is-today' : '',
                  list.length === 0 ? 'is-empty' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="sday-when">
                  <span className="sday-dow">{dayLabel(d).slice(0, 3)}</span>
                  <span className="sday-num">{Number(dayNumber(d))}</span>
                </div>
                {/*
                  AN EMPTY DAY SAYS NOTHING, because saying Nothing booked on
                  five rows out of seven is five sentences that all mean the
                  same thing and none of which is read. The date with clear
                  space beside it is how a paper week says it, and the line
                  under the heading already counts what is on the week.
                */}
                <div className="sday-body">
                  {list.length > 0 &&
                    list.map((v) => {
                      const row = all.find((x) => x.visit.id === v.id)
                      const report = conflicts(v, visits, isAway)
                      const clash = !report.clean
                      const kind = VISIT_KINDS.find((k) => k.id === v.kind)?.label ?? 'Visit'
                      return (
                        <button
                          key={v.id}
                          className={`svisit ${v.state}${clash ? ' has-clash' : ''}`}
                          onClick={() => row && setEditing({ visit: v, job: row.job })}
                        >
                          <span className="svisit-when">{clock(v.from)}</span>
                          <span className="svisit-who">{row ? who(row.job) : 'A job'}</span>
                          <span className="svisit-kind">
                            {kind}
                            {/* Who is going, when anybody has said. */}
                            {(v.assignedTo?.length ?? 0) > 0 && (
                              <>
                                {' '}
                                <i>{crewWording(namesFor(roster, v.assignedTo ?? []))}</i>
                              </>
                            )}
                            {/*
                              HOW LONG, ON THE ROW. It is the number the route
                              planner will add up, and a week read without it
                              is a week you cannot tell a full day from a
                              half hour on.
                            */}
                            {' '}
                            <i>{lengthOf(v)}</i>
                            {v.state === 'requested' && <>{' '}<em>asked for</em></>}
                            {/*
                              THE WORD HAS TO BE THE RIGHT ONE. A red mark
                              reading overlaps on a visit that overlaps
                              nothing, and is only flagged because the person
                              on it is away, sends somebody hunting the week
                              for a booking that is not there.
                            */}
                            {clash && (
                              <>
                                {' '}
                                <b>
                                  {report.clashes.length > 0 && report.awayIds.length > 0
                                    ? 'overlaps, and away'
                                    : report.clashes.length > 0
                                      ? 'overlaps'
                                      : 'away that day'}
                                </b>
                              </>
                            )}
                          </span>
                        </button>
                      )
                    })}
                </div>
              </section>
            )
          })}
          {booked === 0 && (
            <p className="sweek-none">
              Nothing on this week yet. Anything waiting to be booked is listed
              beside it.
            </p>
          )}
        </div>

        <aside className="sched-side">
        {/*
          WHO IS OUT, ON THE SCREEN THAT CARES.

          The week's clash check already refuses to book somebody who is away.
          What it could not do was let you say so: that lived under Settings.
          Saying it here is the difference between a schedule that knows and a
          schedule that finds out on the morning of the install.
        */}
        <div className="card pad sched-away">
          {/*
            The heading and the control were side by side and both wrapped to
            two lines in a 320px rail, which is two two-line objects arguing
            over one row. The control is a row of its own at the foot, the
            same shape as Add a rung.
          */}
          <p className="ws-eyebrow">Who is out</p>

          {(() => {
            const out = roster
              .filter((m) => m.active)
              .map((m) => ({ m, spells: days.map((d) => awayOn(m, d)).filter(Boolean) }))
              .filter((x) => x.spells.length > 0)
            if (roster.filter((m) => m.active).length === 0)
              return (
                <p className="squeue-none">
                  Nobody on the roster yet. Add people under Settings and their names turn up
                  here.
                </p>
              )
            if (out.length === 0)
              return <p className="squeue-none">Everybody is available this week.</p>
            return (
              <ul className="away-list">
                {out.map(({ m, spells }) => (
                  <li key={m.id}>
                    <strong>{m.name.trim() || 'Unnamed'}</strong>
                    <span>{spells[0]?.why?.trim() || 'Out'}</span>
                  </li>
                ))}
              </ul>
            )
          })()}
          {roster.some((m) => m.active) && (
            <button className="quiet-drop away-add" onClick={() => setMarking(true)}>
              Mark somebody out
            </button>
          )}
        </div>


        {/*
          THE QUEUE. A calendar shows what has been decided; this shows what
          has not, and a season is lost to the second one.
        */}
        <div className="card pad sched-queue">

          <p className="ws-eyebrow">Waiting to be booked</p>
          {queue.length === 0 ? (
            <p className="squeue-none">
              {jobs.length === 0
                ? 'Start a house and it shows up here.'
                : 'Every house with an agreed scope has an install booked.'}
            </p>
          ) : (
            <div className="squeue-list">
              {queue.map((q) => {
                const job = jobs.find((j) => j.id === q.job.id)
                return (
                  <div
                    key={q.job.id}
                    className={`squeue-row ${q.kind === 'install' ? 'is-install' : 'is-estimate'}`}
                  >
                    <p className="squeue-who">{who(q.job)}</p>
                    <p className="squeue-said">{q.said}</p>
                    <div className="squeue-acts">
                      <button className="btn" onClick={() => void book(q.job.id, q.kind, days[0])}>
                        Book {q.kind === 'install' ? 'the install' : 'an estimate'}
                      </button>
                      {job && (
                        <button className="link-btn" onClick={() => onOpenJob(job)}>
                          Open
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        </aside>
      </div>

      {/*
        MARKING SOMEBODY OUT, in the shared dialog, from the screen the fact
        is about. It writes the roster, which is the same record Settings
        edits, so the two can never hold different answers.
      */}
      {marking && (
        <AwaySheet
          roster={roster}
          day={days[0]}
          onClose={() => setMarking(false)}
          onSave={(next) => {
            saveCrew(next)
            setRoster(next)
            setMarking(false)
          }}
        />
      )}

      {planning && (

        <DayPlan
          items={jobs}
          day={planning}
          onClose={() => setPlanning(null)}
          onBooked={onChanged}
        />
      )}

      {editing && (
        <VisitSheet
          key={editing.visit.id}
          visit={editing.visit}
          who={who(editing.job)}
          all={visits}
          days={days}
          roster={roster}
          isAway={isAway}
          onClose={() => setEditing(null)}
          onSave={save}
          onDelete={drop}
        />
      )}
    </>
  )
}

/**
 * ONE VISIT, AND WHAT NOBODY HAS CHECKED.
 *
 * SCHED-02 asks for double booking, travel and equipment to block a visit or
 * need an authorized override with a reason. Exactly one of those three has
 * inputs in this product. The danger is not the two that are missing, it is a
 * screen that shows a clean tick as though all three had been looked at, so
 * this prints what it could not check every time, including when it found
 * nothing wrong.
 */
function VisitSheet({
  visit,
  who,
  all,
  days,
  roster,
  isAway,
  onClose,
  onSave,
  onDelete,
}: {
  visit: Visit
  who: string
  all: Visit[]
  days: string[]
  roster: CrewMember[]
  isAway: (id: string, day: string) => boolean
  onClose: () => void
  onSave: (v: Visit) => void
  onDelete: (v: Visit) => void
}) {
  const [v, setV] = useState<Visit>(visit)
  const report = conflicts(v, all, isAway)
  const ok = validWindow(v.from, v.to)

  const hhmm = (min: number) =>
    `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
  const mins = (s: string) => {
    const [h, m] = s.split(':').map(Number)
    return (h || 0) * 60 + (m || 0)
  }

  return (
    <Sheet
      title={who}
      variant="vsheet"
      onClose={onClose}
    >
      {/*
        THE SHEET SCROLLS, THE BUTTONS DO NOT.
        This form is taller than a laptop's 84vh once a clash panel and the
        unchecked list are on it, and the sheet used to scroll as one piece,
        which put Save below the fold with nothing on screen saying so.
        Everything between the title and the actions scrolls; the actions
        are always where the hand expects them.
      */}
      <div className="vsheet-body">
      {/*
        BOOKED ON A DAY SOMEBODY IS OFF. A different problem from a double
        booking and said separately, because the fix is different.
      */}
      {report.awayIds.length > 0 && (
        <div className="vsheet-clash">
          <p className="vsheet-clash-head">
            {report.awayIds.map((id) => nameOf(roster, id)).join(' and ')}{' '}
            {report.awayIds.length === 1 ? 'is' : 'are'} down as away that day.
          </p>
        </div>
      )}
      {/*
        A WARNING GOES ABOVE THE THING IT IS ABOUT. This sat under the form,
        under the state pair, at the bottom of a panel that scrolls, which
        is where you put something you do not mind nobody reading.
      */}
      {report.clashes.length > 0 && (
        <div className="vsheet-clash">
          <p className="vsheet-clash-head">This lands on top of something.</p>
          <ul>
            {report.clashes.map((c) => (
              <li key={c.visit.id}>
                {windowOf(c.visit)} on the same day
                {c.crew === 'shared'
                  ? `, and ${crewWording(namesFor(roster, c.shared))} ${c.shared.length === 1 ? 'is' : 'are'} down for both`
                  : ', and neither says who is going'}
              </li>
            ))}
          </ul>
          {report.clashes.some((c) => c.crew === 'unknown') && (
            <p>
              Put a name on both and this stops guessing. Two visits at the same hour with
              different people on them are two crews working, not a clash.
            </p>
          )}
        </div>
      )}

      <div className="ws-form vsheet-form">
        {/*
          THE CONTROL CARRIES THE NAME AND THE HINT CARRIES THE MEANING.
          Putting the blurb inside the option made the collapsed select read
          Install - Put the lights up, which is a sentence where a value
          belongs. The four kinds still explain themselves, one line under
          the control, for whichever one is chosen.
        */}
        <label className="ws-wide">
          <span>What kind of visit</span>
          <select
            value={v.kind}
            onChange={(e) => setV({ ...v, kind: e.target.value as VisitKind })}
          >
            {VISIT_KINDS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>
          <small className="vsheet-hint">
            {VISIT_KINDS.find((k) => k.id === v.kind)?.blurb}
          </small>
        </label>

        {/*
          ASKED FOR IS NOT AGREED, and it is the second thing to know about
          a visit, not the last. Straight out of the data contract, and the
          reason the two are separate states at all: a dispatcher looking at
          a week has to know which of them he can rely on. It sat under the
          form, below the fold on a laptop, which is no place for the one
          control on this sheet that decides whether a booking is real.
        */}
        <div className="ws-wide vstate-field">
          <span>Where this stands</span>
          <div className="vstate">
            <button
              className={v.state === 'requested' ? 'vstate-on' : ''}
              aria-pressed={v.state === 'requested'}
              onClick={() => setV({ ...v, state: 'requested' })}
            >
              Asked for
            </button>
            <button
              className={v.state === 'confirmed' ? 'vstate-on' : ''}
              aria-pressed={v.state === 'confirmed'}
              onClick={() => setV({ ...v, state: 'confirmed' })}
            >
              Agreed with them
            </button>
          </div>
          <p className="vstate-note">{stateWording(v)}</p>
        </div>

        {/*
          WHO IS GOING. Chips rather than a multi-select, because a crew is
          two or three names and picking two out of a list box on a phone is
          the worst control in the product. Nobody named is a real answer and
          reads as one: the conflict check treats it as unknown rather than
          as safe.
        */}
        <div className="ws-wide vstate-field">
          <span>Who is going</span>
          {roster.filter((m) => m.active).length === 0 ? (
            <p className="hint">
              Nobody on the roster yet. Add people under Team and their names turn up here.
            </p>
          ) : (
            <div className="crewpick">
              {roster
                .filter((m) => m.active)
                .map((m) => {
                  const on = (v.assignedTo ?? []).includes(m.id)
                  const off = isAway(m.id, v.day)
                  return (
                    <button
                      key={m.id}
                      className={on ? 'crewchip on' : 'crewchip'}
                      aria-pressed={on}
                      onClick={() =>
                        setV({
                          ...v,
                          assignedTo: on
                            ? (v.assignedTo ?? []).filter((id) => id !== m.id)
                            : [...(v.assignedTo ?? []), m.id],
                        })
                      }
                    >
                      {m.name.trim() || 'Unnamed'}
                      {off && <em>away</em>}
                    </button>
                  )
                })}
            </div>
          )}
        </div>

        <label className="ws-wide">
          <span>Day</span>
          <select value={v.day} onChange={(e) => setV({ ...v, day: e.target.value })}>
            {days.map((d) => (
              <option key={d} value={d}>
                {dayLabel(d)}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>From</span>
          <input
            type="time"
            value={hhmm(v.from)}
            onChange={(e) => setV({ ...v, from: mins(e.target.value) })}
          />
        </label>

        <label>
          <span>To</span>
          <input
            type="time"
            value={hhmm(v.to)}
            onChange={(e) => setV({ ...v, to: mins(e.target.value) })}
          />
        </label>

        {/* The window said back in words, beside the two inputs that make
            it, so nobody has to read two clocks and subtract. */}
        {ok ? (
          <p className="ws-wide vsheet-len">
            {windowOf(v)} <span>{lengthOf(v)}</span>
          </p>
        ) : (
          <p className="ws-wide vsheet-bad">
            That window runs backwards. The end has to be after the start.
          </p>
        )}

        <label className="ws-wide">
          <span>Anything the crew needs to know</span>
          <textarea
            rows={2}
            value={v.note}
            onChange={(e) => setV({ ...v, note: e.target.value })}
            placeholder="Gate code, the dog, where the ladder goes"
          />
        </label>
      </div>


      </div>

      {/*
        AND THIS ONE DOES NOT SCROLL EITHER.

        It is not a field, it is a standing caveat on the whole sheet, so it
        belongs with the buttons rather than at the end of a form somebody
        may never reach. Set as running prose rather than a heading over
        three bullets, which said the same thing in six lines. It is printed
        every time, including when nothing is wrong, because a screen that
        showed a clean tick for travel, crews and stock would be lying in
        the way that costs a Saturday.
      */}
      <p className="vsheet-unchecked">
        <b>What nothing here has checked.</b> {report.notChecked.join(' ')}
      </p>

      <div className="ws-actions">
        <button className="vsheet-drop" onClick={() => onDelete(v)}>
          Remove
        </button>
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={!ok} onClick={() => onSave(v)}>
          Save
        </button>
      </div>
    </Sheet>
  )
}

/**
 * SOMEBODY IS OUT, SAID IN FOUR FIELDS.
 *
 * Who, from when, until when, and why. The why is optional and is printed on
 * the week beside their name, because "Egor, out" and "Egor, jury duty" are
 * the same fact and only one of them stops you asking.
 *
 * IT REFUSES A SPELL THAT RUNS BACKWARDS rather than writing one nobody can
 * see. An away period whose end is before its start matches no day, so the
 * schedule would go on booking somebody who told you they were out, and the
 * row would sit under their name looking like it had worked.
 */
function AwaySheet({
  roster,
  day,
  onClose,
  onSave,
}: {
  roster: CrewMember[]
  day: string
  onClose: () => void
  onSave: (next: CrewMember[]) => void
}) {
  const able = roster.filter((m) => m.active)
  const [who, setWho] = useState(able[0]?.id ?? '')
  const [from, setFrom] = useState(day)
  const [to, setTo] = useState(day)
  const [why, setWhy] = useState('')

  const spell = { from, to, why }
  const bad = !who
    ? 'Nobody is chosen.'
    : !validAway(spell)
      ? 'That runs backwards. The last day out cannot be before the first.'
      : null

  const save = () => {
    if (bad) return
    onSave(roster.map((m) => (m.id === who ? { ...m, away: [...m.away, spell] } : m)))
  }

  return (
    <Sheet
      title="Somebody is out"
      onClose={onClose}
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save} disabled={!!bad}>
            Mark them out
          </button>
        </>
      }
    >
      <div className="ws-form vsheet-form">
        <label className="ws-wide">
          <span>Who</span>
          <select value={who} onChange={(e) => setWho(e.target.value)}>
            {able.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name.trim() || 'Unnamed'}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>First day out</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          <span>Last day out</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="ws-wide">
          <span>Why, if it helps</span>
          <input
            type="text"
            value={why}
            placeholder="Jury duty, vacation, back"
            onChange={(e) => setWhy(e.target.value)}
          />
        </label>
        {bad && <p className="set-bad ws-wide">{bad}</p>}
      </div>
    </Sheet>
  )
}
